import type { FormatXMLElementFn, PrimitiveType } from 'intl-messageformat'
import { get, startsWith } from 'lodash/fp'
import React from 'react'

import {
  Icon,
  IconProps,
  ToastId,
  UseToastOptions,
  useToast,
} from '@masterplandev/ui'

import { queryErrorHandler } from '../utils/react-query/queryErrorHandler'
import useTranslation from './useTranslation'

type Options = {
  icon?: IconProps['symbol']
  hideDescription?: boolean
  values?: Record<string, PrimitiveType | FormatXMLElementFn<string, string>>
}
/**
 * Creates common toast notifications for server responses.
 * For more advanced use cases just use `useToast()` directly.
 *
 * @param messageIdPrefix - message ID prefix.
 * @returns {object} functions to display toast
 *
 * @example
 * const toast = useServerResponseToast('invite.toast.create')
 * toast.success()
 * toast.error(error)
 *
 * in `messages.yml`
 * invite:
 *   toast:
 *     create:
 *       loading:
 *         title:
 *           en: Processing
 *           de: ...
 *         description:
 *           en: Please wait...
 *           de: ...
 *       success:
 *         title:
 *           en: Success
 *           de: ...
 *         description:
 *           en: An invitation has been successfully sent
 *           de: ...
 *       error:
 *         title:
 *           en: Sorry, something went wrong
 *           de: Entschuldige, hier ist etwas schief gelaufen
 *         description:
 *           en: Please try again
 *           de: Bitte erneut versuchen
 *         message:
 *           ...
 *
 */
export default function useServerResponseToast(messageIdPrefix: string) {
  const toast = useToast()
  const { translate } = useTranslation(messageIdPrefix)
  const toastIdRef = React.useRef<ToastId | undefined>()

  return {
    loading: (options: Options = {}) => {
      toastIdRef.current = toast({
        status: 'loading',
        title: translate('loading.title', options.values),
        description: options.hideDescription
          ? null
          : translate('loading.description', options.values),
      })
    },
    success: (options: Options = {}) => {
      const toastOptions: UseToastOptions = {
        status: 'success',
        title: translate('success.title', options.values),
        description: options?.hideDescription
          ? null
          : translate('success.description', options.values),
        ...(options?.icon && {
          icon: React.createElement(Icon, {
            symbol: options.icon,
            color: 'mpSuccess.600',
            size: 'lg',
          }),
        }),
      }

      if (toastIdRef.current) {
        toast.update(toastIdRef.current, toastOptions)
      } else {
        toast(toastOptions)
      }
    },
    error: (error: unknown, options: Options = {}) => {
      const toastId = get('response.data.message', error)

      const getErrorDescription = () => {
        const message = queryErrorHandler(error)
        if (message) {
          return translate(
            message,
            options.values,
            startsWith('api', message)
              ? { withPrefix: null }
              : { withPrefix: 'api' },
          )
        }

        return translate('error.description', options.values)
      }

      const toastOptions: UseToastOptions = {
        status: 'error',
        title: translate('error.title', options.values),
        description: options?.hideDescription ? null : getErrorDescription(),
        id: toastId,
      }

      if (toast.isActive(toastId)) {
        toast.update(toastId, toastOptions)
      } else {
        toast(toastOptions)
      }
    },
  }
}
